import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import { CloudDownload, Download, Film, GitBranch, HardDrive, Mail, Monitor, Search, type LucideIcon } from 'lucide-react';
import NextImage from 'next/image';
import type { FeatureGridBlock, FeatureGridItem, FeatureIconName, RichSegment } from '../types';
import { RichSegments } from './RichSegments';
import styles from './FeatureGrid.module.css';
interface FeatureGridProps {
  block: FeatureGridBlock;
  blockId: string;
}
export function FeatureGrid(t0) {
  const $ = _c(12);
  const {
    block,
    blockId
  } = t0;
  const columns = block.columns ?? 3;
  let t1;
  if ($[0] !== block.variant) {
    t1 = featureGridVariantClass(block.variant);
    $[0] = block.variant;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const variantClass = t1;
  let t2;
  if ($[2] !== block.intro) {
    t2 = block.intro && block.intro.length > 0 ? <p className={styles.intro}><RichSegments segments={block.intro} /></p> : null;
    $[2] = block.intro;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== block || $[5] !== columns || $[6] !== variantClass) {
    t3 = block.variant === "nvrFeatures" ? <div className={`${styles.grid} ${variantClass}`} data-columns={columns}><div className={styles.nvrFeatureColumn}>{block.items.filter(_temp).map((item_0, idx) => renderItem(item_0, idx, block))}</div><div className={styles.nvrFeatureColumn}>{block.items.filter(_temp2).map((item_2, idx_0) => renderItem(item_2, idx_0, block))}</div></div> : <div className={`${styles.grid} ${variantClass}`} data-columns={columns} style={{
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
    }}>{block.items.map((item_3, idx_1) => renderItem(item_3, idx_1, block))}</div>;
    $[4] = block;
    $[5] = columns;
    $[6] = variantClass;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== blockId || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className={styles.wrapper} data-cms-block-id={blockId} data-cms-field="featureGrid">{t2}{t3}</div>;
    $[8] = blockId;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp2(item_1) {
  return item_1.featureColumn === 2;
}
function _temp(item) {
  return item.featureColumn !== 2;
}
function featureGridVariantClass(variant: FeatureGridBlock['variant']): string {
  const fallbackClass = styles.card ?? '';
  switch (variant) {
    case 'bare':
      return styles.bare ?? fallbackClass;
    case 'media':
      return styles.media ?? fallbackClass;
    case 'ctaButtons':
      return styles.ctaButtons ?? fallbackClass;
    case 'imageComparison':
      return styles.imageComparison ?? fallbackClass;
    case 'wellCards':
      return styles.wellCards ?? fallbackClass;
    case 'valuePillars':
      return styles.valuePillars ?? fallbackClass;
    case 'businessChoice':
      return styles.businessChoice ?? fallbackClass;
    case 'productCards':
      return styles.productCards ?? fallbackClass;
    case 'nvrFeatures':
      return styles.nvrFeatures ?? fallbackClass;
    case 'card':
    default:
      return fallbackClass;
  }
}
function renderItem(item: FeatureGridItem, idx: number, block: FeatureGridBlock): JSX.Element {
  return <article key={item.id ?? idx} className={styles.item}>
      {(block.variant === 'media' || block.variant === 'imageComparison') && item.heading ? <h3 className={styles.heading}>{item.heading}</h3> : null}
      {item.image ? <div className={styles.imageWrapper}>
          {item.image.width && item.image.height ? <NextImage src={item.image.src} alt={item.image.alt} width={item.image.width} height={item.image.height} className={imageClassName(item.imageShape)} data-src={item.image.sourceSrc ?? item.image.src} /> :
      // eslint-disable-next-line @next/next/no-img-element
      <img src={item.image.src} alt={item.image.alt} className={imageClassName(item.imageShape)} loading="lazy" data-src={item.image.sourceSrc ?? item.image.src} />}
        </div> : null}
      {item.iconName ? <FeatureIcon name={item.iconName} /> : null}
      {item.icon ? <div className={styles.iconWrapper}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={item.icon.src} alt={item.icon.alt} className={styles.icon} loading="lazy" data-src={item.icon.sourceSrc ?? item.icon.src} />
        </div> : null}
      {block.variant !== 'media' && block.variant !== 'imageComparison' && item.heading ? <h3 className={styles.heading}>{item.heading}</h3> : null}
      {item.secondaryHeading ? <h3 className={styles.secondaryHeading}>{item.secondaryHeading}</h3> : null}
      {item.body && item.body.length > 0 ? bodyParagraphs(item.body, Boolean(block.preserveLineBreaks)).map((segments, paragraphIndex) => <p key={paragraphIndex} className={styles.body}>
              <RichSegments segments={segments} />
            </p>) : null}
      {item.cta ? <a href={item.cta.href} target={item.cta.target} rel={item.cta.rel} className={item.cta.variant === 'button' ? styles.itemCtaButton : item.cta.variant === 'outlineButton' ? styles.itemCtaOutlineButton : styles.itemCta}>
          {item.cta.label}
        </a> : null}
    </article>;
}
const featureIcons: Record<FeatureIconName, LucideIcon> = {
  search: Search,
  cloudDownload: CloudDownload,
  download: Download,
  mail: Mail,
  hardDrive: HardDrive,
  film: Film,
  monitor: Monitor,
  gitBranch: GitBranch
};
function FeatureIcon(t0) {
  const $ = _c(2);
  const {
    name
  } = t0;
  const Icon = featureIcons[name];
  let t1;
  if ($[0] !== Icon) {
    t1 = <span className={styles.featureIcon} aria-hidden="true"><Icon className={styles.featureIconSvg} strokeWidth={3} /></span>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function imageClassName(imageShape: FeatureGridBlock['items'][number]['imageShape']): string {
  return [styles.image, imageShape === 'circle' ? styles.circleImage : null].filter(Boolean).join(' ');
}
function bodyParagraphs(segments: RichSegment[], preserveLineBreaks: boolean): RichSegment[][] {
  if (preserveLineBreaks) {
    return [segments];
  }
  const paragraphs: RichSegment[][] = [[]];
  for (const segment of segments) {
    if (segment.kind === 'lineBreak') {
      if (paragraphs[paragraphs.length - 1]!.length > 0) {
        paragraphs.push([]);
      }
      continue;
    }
    paragraphs[paragraphs.length - 1]!.push(segment);
  }
  return paragraphs.filter(paragraph => paragraph.length > 0);
}